import type { IntegrationState } from '@/features/integrations/integrations.types';
import { useContactsState } from '@/integrations/useContactsState';
import { useEventKitState } from '@/integrations/useEventKitState';
import { useTerminalIntegrationState } from '@/integrations/useTerminalIntegrationState';

/**
 * Backend-served on-device catalog entries the client knows how to operate:
 * permission status feeds `isConnected`, and connect/disconnect route through
 * the matching branches in `useIntegrationActions`. Entries NOT listed here
 * render with the settings gear only (forward compat with future catalog ids).
 */
export const CONNECTABLE_DEVICE_TOOLKITS = new Set([
  'applecontacts',
  'applereminders',
  'terminal',
]);

export const isConnectableDeviceToolkit = (toolkitName: string): boolean =>
  CONNECTABLE_DEVICE_TOOLKITS.has(toolkitName);

/** Beta labeling the backend catalog does not model. */
export const DEVICE_INTEGRATION_BETA_LABELS: Record<
  string,
  Pick<IntegrationState, 'isBeta' | 'betaTooltip'>
> = {
  applecontacts: {
    isBeta: true,
    betaTooltip: 'Used for context collection and managing your contacts.',
  },
};

export function useDevicePermissionGrants(): Record<string, boolean> {
  const { contactsGranted } = useContactsState();
  const { remindersGranted } = useEventKitState();
  const { terminalEnabled } = useTerminalIntegrationState();
  return {
    applecontacts: contactsGranted,
    applereminders: remindersGranted,
    terminal: terminalEnabled,
  };
}
